import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EventsListModal from './EventsListModal';
import LeadSearchInput from '../leads/LeadSearchInput';
import ManageStudentsModal from './ManageStudentsModal';
import AttendanceModal from './AttendanceModal';
import EventFormModal from './EventFormModal';
import EventDetailsModal from './EventDetailsModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import RecurringDeleteModal from './RecurringDeleteModal';
import RecurringEditModal from './RecurringEditModal';
import MakeupEventModal from './MakeupEventModal';
import BulkEmailModal from './BulkEmailModal';
import { hasPermission, getCalendarViewAccess } from '../../utils/permissions';
import { useNotification, Button } from '../common';
import useModuleSettings from '../../hooks/useModuleSettings';

// Custom styles for react-big-calendar to fix height issue
const styles = `
  /* Make event blocks smaller and fit better */
  .rbc-event {
    font-size: 12px !important;
    padding: 2px 3px !important;
    margin: 1px 0 !important;
  }
  
  /* Make rows auto-adjust height to fit content */
  .rbc-month-row {
    min-height: 100px !important;
    height: auto !important;
    display: inline-table !important;
    flex: 0 0 0 !important;
    table-layout: fixed !important;
    width: 100% !important;
  }
  
  /* Make each day column equal width */
  .rbc-row-bg {
    display: flex !important;
  }
  
  .rbc-day-bg {
    flex: 1 1 0 !important;
    max-width: calc(100% / 7) !important;
  }
  
  /* Fix column headers to same width */
  .rbc-header {
    flex: 1 1 0 !important;
    max-width: calc(100% / 7) !important;
  }
  
  /* Make month view auto-adjust to content */
  .rbc-month-view {
    height: auto !important;
    width: 100% !important;
  }
  
  /* Ensure rows can display all events */
  .rbc-row-content {
    height: auto !important;
    width: 100% !important;
  }
  
  /* Constrain date cells */
  .rbc-date-cell {
    max-width: 100% !important;
  }
`;

// Setup the localizer for react-big-calendar with Monday as first day
moment.updateLocale('en', { week: { dow: 1 } }); // Set Monday as first day of week
const localizer = momentLocalizer(moment);

// Event types with corresponding colors
const eventTypeColors = {
  course: '#10B981',  // green (primary event type)
  demo: '#3B82F6',    // blue
  'sales call': '#F59E0B'  // amber/orange for sales calls
};

// tutorColors will be managed as state inside the component

// Recurrence frequency options
const recurrenceFrequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' }
];

const CalendarView = () => {
  // Import useNotification hook
  const { showNotification } = useNotification();
  
  // Check if Outlook Calendar module is enabled
  const { moduleSettings } = useModuleSettings();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({
    title: '',
    description: '',
    start_time: new Date(),
    end_time: new Date(moment().add(1, 'hour').toDate()),
    location: '',
    country: '',
    event_type: 'course', // Default to course (renamed from class)
    related_lead_id: null,
    related_deal_id: null,
    is_recurring: false,
    recurrence_count: 1,
    recurrence_frequency: 'weekly',
    tutor_id: null,  // Add tutor_id field for course events
    course_id: null,  // Add course_id field for course events
    send_tutor_reminder: true,  // Default to send tutor reminders
    send_student_reminder: true,  // Default to send student reminders
    status: 'Scheduled'  // Default status is Scheduled
  });
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tutors, setTutors] = useState([]); // State to store tutors (employees with role='tutor')
  const [courses, setCourses] = useState([]); // State to store courses from settings
  const [viewingEvent, setViewingEvent] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showRecurringEditModal, setShowRecurringEditModal] = useState(false);
  const [showRecurringDeleteModal, setShowRecurringDeleteModal] = useState(false);
  const [editRecurringMode, setEditRecurringMode] = useState('single'); // 'single', 'future', or 'all'
  const [deleteRecurringMode, setDeleteRecurringMode] = useState('single'); // 'single', 'future', or 'all'
  const [calendarDate, setCalendarDate] = useState(new Date()); // Track current calendar date
  const [showFilters, setShowFilters] = useState(false); // Toggle for filter panel
  const [showEventsListModal, setShowEventsListModal] = useState(false);
  const [showManageStudentsModal, setShowManageStudentsModal] = useState(false); // Toggle for events list modal
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false); // Toggle for bulk email modal
  const [studentEnrollment, setStudentEnrollment] = useState({
    currentCount: 0,
    maxStudents: 10
  });
  const [sendingReminders, setSendingReminders] = useState(false); // Track reminder sending state
  const [showAttendanceModal, setShowAttendanceModal] = useState(false); // Toggle for attendance modal
  const [showMakeupModal, setShowMakeupModal] = useState(false); // Toggle for makeup event modal
  const [showOutlookEventModal, setShowOutlookEventModal] = useState(false); // Toggle for Outlook event modal
  const [selectedOutlookEvent, setSelectedOutlookEvent] = useState(null); // Selected Outlook event
  const [tutorColors, setTutorColors] = useState({}); // Store tutor colors in state
  
  // Outlook Calendar integration state
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookLoading, setOutlookLoading] = useState(false);
  
  // Initialize filters from localStorage or use defaults
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('calendarFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      country: '',
      location: '',
      tutor_id: '',
      course_id: '',
      event_type: '',
      student_name: '', // Filter for student name
      parent_email: '', // New filter for parent email
      recurring_start_only: false, // Filter for only showing first events in recurring series
      recurring_end_only: false // Filter for only showing last events in recurring series
    };
  });
  
  // Initialize active filters from localStorage or use empty object
  const [activeFilters, setActiveFilters] = useState(() => {
    const savedActiveFilters = localStorage.getItem('calendarActiveFilters');
    return savedActiveFilters ? JSON.parse(savedActiveFilters) : {};
  });

  // State for company countries (quick filters)
  const [companyCountries, setCompanyCountries] = useState([]);
  
  // Check Outlook connection status
  const checkOutlookConnection = async () => {
    if (!moduleSettings.outlookCalendar) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/outlook-calendar/status', {
        headers: { 'x-auth-token': token }
      });
      
      setOutlookConnected(response.data.connected && !response.data.is_expired);
    } catch (error) {
      console.error('Error checking Outlook connection:', error);
      setOutlookConnected(false);
    }
  };
  
  // Fetch Outlook events
  const fetchOutlookEvents = async (startDate, endDate) => {
    if (!moduleSettings.outlookCalendar || !outlookConnected) {
      return [];
    }
    
    try {
      setOutlookLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/outlook-calendar/events', {
        headers: { 'x-auth-token': token },
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      return response.data.events || [];
    } catch (error) {
      console.error('Error fetching Outlook events:', error);
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        setOutlookConnected(false);
      }
      return [];
    } finally {
      setOutlookLoading(false);
    }
  };
  
  // Fetch events from API
  const fetchEvents = async (dateToShow = null) => {
    try {
      // Check calendar view access level
      const viewAccess = getCalendarViewAccess();
      if (viewAccess === 'none') {
        setError('You do not have permission to view calendar events');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        },
        params: { ...activeFilters }
      };
      
      console.log("Fetching events with params:", config.params);
      
      // Use the provided date or the current calendar date
      const dateToUse = dateToShow || calendarDate;
      
      // Get date range based on current calendar view
      // Use a smaller window based on the current view
      const view = config.params.view_type || 'month';
      let startDate, endDate;
      
      if (view === 'day') {
        // For day view: just get +/- 1 day
        startDate = moment(dateToUse).subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
        endDate = moment(dateToUse).add(1, 'days').endOf('day').format('YYYY-MM-DD');
      } else if (view === 'week') {
        // For week view: get +/- 2 weeks
        startDate = moment(dateToUse).subtract(2, 'weeks').startOf('week').format('YYYY-MM-DD');
        endDate = moment(dateToUse).add(2, 'weeks').endOf('week').format('YYYY-MM-DD');
      } else {
        // For month view: get +/- 1 month
        startDate = moment(dateToUse).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
        endDate = moment(dateToUse).add(1, 'months').endOf('month').format('YYYY-MM-DD');
      }
      
      // Add date range and view type to params
      config.params.start_date = startDate;
      config.params.end_date = endDate;
      config.params.view_type = view;
      config.params.page = 1;  // First page
      config.params.limit = 500; // Batch size
      
      const res = await axios.get('/api/calendar', config);
      
      // Add logging to debug API response
      console.log('API Response - pagination:', res.data.pagination);
      
      if (!res.data.events) {
        console.error('Unexpected API response format:', res.data);
        setError('Received unexpected data format from server');
        setLoading(false);
        return;
      }
      
      // Using events directly from the paginated response
      const events = res.data.events;
      console.log(`Received ${events.length} events of ${res.data.pagination.total} total`);
      
      // Format events for the calendar - first collect all course event IDs for batch request
      const courseEvents = events.filter(event => event.event_type === 'course');
      const courseEventIds = courseEvents.map(event => event.id);
      
      // Create a map to store enrollment data for all course events
      let enrollmentDataMap = {};
      
      // Fetch enrollment data for all course events in one batch request
      if (courseEventIds.length > 0) {
        try {
          const token = localStorage.getItem('token');
          const enrollmentResponse = await axios.post('/api/calendar/batch/students', 
            { event_ids: courseEventIds },
            { headers: { 'x-auth-token': token } }
          );
          
          // Create a map of event ID to enrollment data
          if (enrollmentResponse.data && enrollmentResponse.data.enrollment_data) {
            enrollmentDataMap = enrollmentResponse.data.enrollment_data.reduce((map, data) => {
              map[data.event_id] = {
                currentCount: data.current_count,
                maxStudents: data.max_students
              };
              return map;
            }, {});
          }
          
          console.log(`Batch fetched enrollment data for ${courseEventIds.length} events`);
        } catch (error) {
          console.error('Error fetching batch enrollment data:', error);
        }
      }
      
      // Format events for the calendar - no need for async/await anymore
      const formattedEvents = events.map(event => {
        // Default enrollment data
        let studentEnrollmentData = { currentCount: 0, maxStudents: 10 };
        
        // For course events, use the data from the batch request
        if (event.event_type === 'course' && enrollmentDataMap[event.id]) {
          studentEnrollmentData = enrollmentDataMap[event.id];
        }
        
        return {
          id: event.id,
          title: event.title,
          start: new Date(event.start_time),
          end: new Date(event.end_time),
          description: event.description,
          location: event.location,
          country: event.country,
          event_type: event.event_type,
          course_id: event.course_id ? parseInt(event.course_id, 10) : null,
          course_name: event.course_name,
          related_lead_id: event.related_lead_id,
          related_deal_id: event.related_deal_id,
          lead_name: event.lead_name,
          deal_title: event.deal_title,
          created_by_name: event.created_by_name,
          tutor_id: event.tutor_id ? parseInt(event.tutor_id, 10) : null,
          tutor_name: event.tutor_name,
          // Enhanced handling for recurring_id
          recurring_id: (event.recurring_id && event.recurring_id !== '') ? event.recurring_id : null,
          is_recurring_start: event.is_recurring_start || false,
          backgroundColor: eventTypeColors[event.event_type] || eventTypeColors.other,
          // Student enrollment data for course events
          studentEnrollment: studentEnrollmentData,
          // Event status
          status: event.status || 'Scheduled',
          // Makeup event relationship
          original_event: event.original_event,
          makeup_event: event.makeup_event,
          is_makeup: event.makeup_for_event_id ? true : false,
          has_makeup: event.has_makeup_event,
          // Public booking flag
          is_public: event.is_public || false,
          // Reminder settings
          send_tutor_reminder: event.send_tutor_reminder || false,
          send_student_reminder: event.send_student_reminder || false
        };
      });
      
      // Fetch Outlook events if module is enabled and user is connected
      let allEvents = formattedEvents;
      if (moduleSettings.outlookCalendar && outlookConnected) {
        const outlookEventsData = await fetchOutlookEvents(startDate, endDate);
        setOutlookEvents(outlookEventsData);
        
        // Add outlook events to the calendar with visual distinction
        const formattedOutlookEvents = outlookEventsData.map(event => ({
          ...event,
          backgroundColor: '#0078D4', // Microsoft blue
          borderLeftColor: '#0078D4',
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          source: 'outlook',
          readonly: true
        }));
        
        allEvents = [...formattedEvents, ...formattedOutlookEvents];
      }
      
      setEvents(allEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to fetch events');
      setLoading(false);
    }
  };
  
  // Fetch courses from settings
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get('/api/settings/courses/active', config);
      setCourses(response.data);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };
  
  // Fetch leads, deals, and tutors for the dropdowns
  const fetchLeadsAndDeals = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch leads
      const leadsRes = await axios.get('/api/leads', config);
      setLeads(leadsRes.data.leads || []);
      
      // Fetch deals
      const dealsRes = await axios.get('/api/deals', config);
      setDeals(dealsRes.data.deals || []);
      
      // Fetch tutors (employees with role='tutor')
      // Add role=tutor and limit=100 query parameters to fetch only tutors and bypass pagination
      const tutorsRes = await axios.get('/api/auth/employees', {
        ...config,
        params: { ...config.params, role: 'tutor', limit: 100 }
      });
      // Backend now filters by role, no need for frontend filter
      const tutorsList = tutorsRes.data.employees || [];
      
      // Log how many tutors were loaded (backend filtered)
      console.log(`Loaded ${tutorsList.length} tutors (filtered by backend) for calendar - IDs: ${tutorsList.map(t => t.id).join(', ')}`);
      
      // Generate unique colors for each tutor
      // Expanded color palette to handle more tutors - no red since we use it for unassigned courses
      const colors = [
        '#3B82F6', // blue
        '#EC4899', // pink
        '#8B5CF6', // purple
        '#F59E0B', // amber
        '#06B6D4', // cyan
        '#10B981', // green
        '#6366F1', // indigo
        '#F97316', // orange
        '#14B8A6', // teal
        '#A855F7', // violet
        '#84CC16', // lime
        '#0369A1', // sky blue
        '#15803D', // forest green
        '#9D174D', // fuchsia
        '#7E22CE', // violet
        '#B45309', // amber dark
        '#BE185D', // rose
        '#064E3B', // emerald
        '#0E7490', // cyan dark
        '#0F766E', // teal dark
        '#1E40AF', // blue dark
        '#374151', // gray dark
        '#4B5563', // gray
        '#6B7280', // gray light
      ];
      
      // Assign colors to tutors - create a new object to update state properly
      const newTutorColors = {};
      tutorsList.forEach((tutor, index) => {
        // Use modulo to cycle through colors if there are more tutors than colors
        newTutorColors[tutor.id] = colors[index % colors.length];
      });
      
      // Use the setter from useState to update tutorColors
      setTutorColors(newTutorColors);
      setTutors(tutorsList);
    } catch (err) {
      console.error('Error fetching leads, deals, and tutors:', err);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox type inputs
    if (type === 'checkbox') {
      setFilters(prev => ({
        ...prev,
        [name]: checked
      }));
    } 
    // Handle all other inputs
    else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Apply filters with localStorage persistence
  const applyFilters = () => {
    // Only include filters with values (string values should not be empty, boolean values are included if true)
    const newActiveFilters = Object.entries(filters)
      .filter(([_, value]) => {
        if (typeof value === 'boolean') {
          return value === true;
        }
        return value !== '';
      })
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    
    // Save to localStorage
    localStorage.setItem('calendarActiveFilters', JSON.stringify(newActiveFilters));
    localStorage.setItem('calendarFilters', JSON.stringify(filters));
    
    setActiveFilters(newActiveFilters);
    
    // Close filters panel on mobile
    if (window.innerWidth < 768) {
      setShowFilters(false);
    }
  };
  
  // Clear filters and remove from localStorage
  const clearFilters = () => {
    const emptyFilters = {
      country: '',
      location: '',
      tutor_id: '',
      course_id: '',
      event_type: '',
      student_name: '',
      parent_email: '',
      recurring_start_only: false,
      recurring_end_only: false
    };
    
    // Clear localStorage
    localStorage.removeItem('calendarActiveFilters');
    localStorage.setItem('calendarFilters', JSON.stringify(emptyFilters));
    
    setFilters(emptyFilters);
    setActiveFilters({});
  };
  
  // Quick filter by country with localStorage persistence
  const quickFilterByCountry = (country) => {
    // If already filtered by this country, clear the filter
    if (activeFilters.country === country) {
      const newActiveFilters = { ...activeFilters };
      delete newActiveFilters.country;
      
      // Update localStorage
      localStorage.setItem('calendarActiveFilters', JSON.stringify(newActiveFilters));
      
      const newFilters = { ...filters, country: '' };
      localStorage.setItem('calendarFilters', JSON.stringify(newFilters));
      
      setActiveFilters(newActiveFilters);
      setFilters(newFilters);
    } 
    // Otherwise, set the country filter
    else {
      const newFilters = { ...filters, country };
      const newActiveFilters = { ...activeFilters, country };
      
      // Update localStorage
      localStorage.setItem('calendarFilters', JSON.stringify(newFilters));
      localStorage.setItem('calendarActiveFilters', JSON.stringify(newActiveFilters));
      
      setFilters(newFilters);
      setActiveFilters(newActiveFilters);
    }
  };
  
  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  // Function to fetch company countries for quick filters
  const fetchCompanyCountries = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };

      const res = await axios.get('/api/settings/company/countries', config);
      setCompanyCountries(res.data);
    } catch (error) {
      console.error('Error fetching company countries:', error);
      // Default to Norway, Sweden, Germany if there's an error
      setCompanyCountries(['Norway', 'Sweden', 'Germany']);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchLeadsAndDeals();
    fetchCourses();
    fetchCompanyCountries();
    
    // Check Outlook connection if module is enabled
    if (moduleSettings.outlookCalendar) {
      checkOutlookConnection();
    }
  }, [moduleSettings.outlookCalendar]);
  
  // Fetch events when filters change
  useEffect(() => {
    fetchEvents();
  }, [activeFilters]);
  
  // Fetch events when Outlook connection status changes
  useEffect(() => {
    if (moduleSettings.outlookCalendar) {
      fetchEvents();
    }
  }, [outlookConnected]);
  
  // Handle event click
  const handleEventClick = async (event) => {
    // Handle Outlook events differently (read-only)
    if (event.source === 'outlook') {
      setSelectedOutlookEvent(event);
      setShowOutlookEventModal(true);
      return;
    }
    
    // Get current user and check permissions
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Check calendar view access level
    const viewAccess = getCalendarViewAccess();
    console.log('Calendar view access level:', viewAccess);

    if (viewAccess === 'none') {
      alert('You do not have permission to view calendar event details');
      return;
    }

    // For users with 'own' access level, check if this is their event
    if (viewAccess === 'own' && event.tutor_id !== user.id) {
      alert('You do not have permission to view events that are not assigned to you');
      return;
    }

    // If we get here, the user has permission to view the event details
    
    // Debug logging to check if recurring_id is present
    console.log('Clicked event:', event);
    
    // Ensure we properly handle the recurring_id property
    // Sometimes it can be null, undefined, or empty string
    const hasRecurringId = event.recurring_id && event.recurring_id !== '';
    console.log('Event has recurring_id:', hasRecurringId ? 'Yes - ' + event.recurring_id : 'No');
    
    // Create a clean copy of the event with properly handled recurring_id
    const cleanEvent = {
      ...event,
      // Ensure recurring_id is either a valid value or null (not undefined or '')
      recurring_id: hasRecurringId ? event.recurring_id : null
    };
    
    // Get full event details from the backend to ensure we have the latest data
    // including attendance data for completed courses
    try {
      const token = localStorage.getItem('token');
      const eventDetailsResponse = await axios.get(`/api/calendar/${event.id}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      // Log full event details from API for debugging
      console.log('Fetched detailed event data:', eventDetailsResponse.data);
      
      // Merge the detailed event data with our cleaned event
      const detailedEvent = {
        ...cleanEvent,
        ...eventDetailsResponse.data,
        // Keep the client-specific fields from cleanEvent
        start: cleanEvent.start, 
        end: cleanEvent.end,
        // Ensure is_public is properly set
        is_public: eventDetailsResponse.data.is_public || false
      };
      
      // Add debug log for is_public field 
      console.log(`Event ${event.id} is_public value from API:`, eventDetailsResponse.data.is_public);
      
      // Parse attendance_data if it exists and is a string
      if (detailedEvent.attendance_data && typeof detailedEvent.attendance_data === 'string') {
        try {
          detailedEvent.attendance_data = JSON.parse(detailedEvent.attendance_data);
        } catch (e) {
          console.warn('Failed to parse attendance data:', e);
          detailedEvent.attendance_data = null;
        }
      }
      
      setViewingEvent(detailedEvent);
    } catch (error) {
      console.error('Error fetching detailed event data:', error);
      setViewingEvent(cleanEvent);
    }
    
    setShowViewModal(true);
    
    // Fetch student enrollment data if this is a course event
    if (event.event_type === 'course') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/calendar/${event.id}/students`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        setStudentEnrollment({
          currentCount: response.data.current_count || 0,
          maxStudents: response.data.max_students || 10,
          students: response.data.students || []
        });
      } catch (error) {
        console.error('Error fetching student enrollment:', error);
        setStudentEnrollment({
          currentCount: 0,
          maxStudents: 10,
          students: []
        });
      }
    } else {
      // Reset student enrollment for non-course events
      setStudentEnrollment({
        currentCount: 0,
        maxStudents: 10,
        students: []
      });
    }
  };
  
  // Handle slot select for creating new event
  const handleSlotSelect = ({ start, end }) => {
    // Create a new date object based on the selected date
    const defaultStartTime = new Date(start);
    defaultStartTime.setHours(17, 0, 0); // Set to 17:00 (5:00 PM)
    
    const defaultEndTime = new Date(start);
    defaultEndTime.setHours(18, 30, 0); // Set to 18:30 (6:30 PM)
    
    setCurrentEvent({
      title: '',
      description: '',
      start_time: defaultStartTime,
      end_time: defaultEndTime,
      location: '',
      country: '',
      event_type: 'course', // Default to course
      related_lead_id: null,
      related_deal_id: null,
      is_recurring: false,
      recurrence_count: 1,
      recurrence_frequency: 'weekly',
      tutor_id: null,
      course_id: null,
      status: 'Scheduled', // Default status for new events
      send_tutor_reminder: true,
      send_student_reminder: true
    });
    setIsEditing(false);
    setShowEventModal(true);
  };
  
  // Handle calendar navigation (when user changes months/weeks/days)
  const handleCalendarNavigate = (date, view) => {
    setCalendarDate(date);
    
    // Update view type in active filters
    const updatedFilters = { ...activeFilters, view_type: view };
    setActiveFilters(updatedFilters);
    
    // Save updated filters to localStorage
    localStorage.setItem('calendarActiveFilters', JSON.stringify(updatedFilters));
    
    // Fetch events with new date and view
    fetchEvents(date);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: checked
      }));
    } 
    // Handle number inputs
    else if (type === 'number') {
      // Allow empty string to clear the input before typing a new number
      if (value === '') {
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value  // Store empty string temporarily while typing
        }));
      } else {
        setCurrentEvent(prev => ({
          ...prev,
          [name]: parseInt(value, 10) || 1
        }));
      }
    } 
    // Handle event type change
    else if (name === 'event_type') {
      if (value === 'course') {
        // For course events, we need to clear related lead and deal
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value,
          related_lead_id: null,
          related_deal_id: null
        }));
      } else if (value === 'demo' || value === 'sales call') {
        // For demo and sales call events, handle specially:
        // 1. Clear course-specific fields
        // 2. Clear recurring event settings (not allowed for sales calls and demos)
        // 3. Also clear related_deal_id (only related_lead_id is used)
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value,
          course_id: null,
          tutor_id: null,
          is_recurring: false,
          recurrence_count: 1,
          related_deal_id: null
        }));
      } else {
        // For any other event types
        setCurrentEvent(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
    // Handle course_id specially to ensure it's stored as a number
    // and to set the default max_students from the selected course
    else if (name === 'course_id') {
      const courseId = value ? parseInt(value, 10) : null;
      
      setCurrentEvent(prev => {
        // If a course is selected, find its max_students
        if (courseId) {
          const selectedCourse = courses.find(c => c.id === courseId);
          if (selectedCourse) {
            return {
              ...prev,
              [name]: courseId,
              // Set max_students to the course default if not already set
              max_students: prev.max_students ? prev.max_students : selectedCourse.max_students
            };
          }
        }
        
        return {
          ...prev,
          [name]: courseId
        };
      });
    }
    // Handle tutor_id specially to ensure it's stored as a number
    else if (name === 'tutor_id') {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : null
      }));
    }
    // Handle all other inputs
    else {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // States for event management
  
  // Handle date and time changes
  const handleDateChange = (field, date) => {
    // Create a copy of current event data
    const updatedEvent = {
      ...currentEvent,
      [field]: date
    };
    
    // Check for invalid time combinations (end time before or equal to start time)
    if (field === 'end_time' && new Date(updatedEvent.end_time) <= new Date(updatedEvent.start_time)) {
      // If end time is being set to before start time, show a warning
      alert('End time must be after start time');
      
      // Set a valid end time (start time + 30 minutes)
      const newEndTime = new Date(updatedEvent.start_time);
      newEndTime.setMinutes(newEndTime.getMinutes() + 30);
      updatedEvent.end_time = newEndTime;
    }
    
    // If start time is being changed to after end time, adjust end time
    if (field === 'start_time' && new Date(updatedEvent.start_time) >= new Date(updatedEvent.end_time)) {
      // Set end time to start time + 30 minutes
      const newEndTime = new Date(updatedEvent.start_time);
      newEndTime.setMinutes(newEndTime.getMinutes() + 30);
      updatedEvent.end_time = newEndTime;
    }
    
    // Update state with validated times
    setCurrentEvent(updatedEvent);
  };
  
  // Create or update event
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    
    // Check relevant permissions based on whether creating or editing
    if (isEditing) {
      if (!hasPermission('calendar', 'update')) {
        showNotification('You do not have permission to update calendar events', 'error');
        return;
      }
    } else {
      if (!hasPermission('calendar', 'create')) {
        showNotification('You do not have permission to create calendar events', 'error');
        return;
      }
    }
    
    // Validate that end time is after start time
    const startTime = new Date(currentEvent.start_time);
    const endTime = new Date(currentEvent.end_time);
    
    if (endTime <= startTime) {
      showNotification('End time must be after start time. Please adjust the times.', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Format data for API
      const eventData = {
        ...currentEvent,
        // Different handling for different event types
        course_id: currentEvent.event_type === 'course' ? (currentEvent.course_id ? parseInt(currentEvent.course_id) : null) : null,
        tutor_id: currentEvent.event_type === 'course' ? (currentEvent.tutor_id ? parseInt(currentEvent.tutor_id) : null) : null,
        related_lead_id: currentEvent.event_type !== 'course' ? (currentEvent.related_lead_id ? parseInt(currentEvent.related_lead_id) : null) : null,
        related_deal_id: currentEvent.event_type !== 'course' ? (currentEvent.related_deal_id ? parseInt(currentEvent.related_deal_id) : null) : null,
        // Ensure recurrence count is a number and at least 1
        recurrence_count: currentEvent.recurrence_count === '' ? 1 : Math.max(parseInt(currentEvent.recurrence_count) || 1, 1)
      };
      
      // If this is a course event, we should only add auto-managed information for NEW events
      // For EDITED events, the backend will handle updating course/tutor info to avoid duplication
      if (currentEvent.event_type === 'course' && !isEditing) {
        // Start with existing description or empty string
        const existingDescription = eventData.description || '';
        const separator = existingDescription ? '\n\n' : '';
        
        // Start building the class info section
        let classInfo = '';
        
        // Add course information if selected
        if (eventData.course_id) {
          const selectedCourse = courses.find(course => course.id === parseInt(eventData.course_id));
          if (selectedCourse) {
            classInfo += `\n\nCourse: ${selectedCourse.name} (${selectedCourse.age_group}, ${selectedCourse.level})`;
            if (selectedCourse.course_format) {
              classInfo += ` - ${selectedCourse.course_format}`;
            }
            
            // Add capacity information if available
            if (selectedCourse.max_students) {
              classInfo += `\nCapacity: ${selectedCourse.max_students} students`;
            }
          }
        }
        
        // Add tutor information if selected
        if (eventData.tutor_id) {
          const selectedTutor = tutors.find(tutor => tutor.id === parseInt(eventData.tutor_id));
          if (selectedTutor) {
            classInfo += `\n\nAssigned Tutor: ${selectedTutor.name}`;
            // Add tutor details if available
            if (selectedTutor.university) {
              classInfo += ` (${selectedTutor.university}`;
              if (selectedTutor.study_program) {
                classInfo += `, ${selectedTutor.study_program}`;
              }
              classInfo += `)`;
            }
          }
        }
        
        // Only add class info if we have some content
        if (classInfo) {
          eventData.description = existingDescription + separator + classInfo;
        }
      } 
      // If editing a course event, we need to clean up the description 
      // to remove any previously added course/tutor info
      else if (currentEvent.event_type === 'course' && isEditing) {
        const originalDesc = eventData.description || '';
        
        // Look for automatically added course info
        const courseInfoIndex = originalDesc.indexOf('Course:');
        const tutorInfoIndex = originalDesc.indexOf('Assigned Tutor:');
        
        // Find the earliest auto-added content marker
        const firstAutoIndex = [courseInfoIndex, tutorInfoIndex]
          .filter(index => index !== -1)
          .sort((a, b) => a - b)[0];
        
        // If we find auto-generated content, clean the description
        if (firstAutoIndex !== undefined) {
          // Keep only the user part of the description (before automated info)
          eventData.description = originalDesc.substring(0, firstAutoIndex).trim();
        }
      }
      
      // Optionally update the event title to indicate it's a group class
      // Only do this for new events, not when editing
      if (!isEditing && !eventData.title.toLowerCase().includes('group') && 
          !eventData.title.toLowerCase().includes('class')) {
          // Initial student count is 0, as students will be added through the management modal
          const studentCount = 0;
          
          // Get max students if a course is selected
          let maxStudents = null;
          if (eventData.course_id) {
            const selectedCourse = courses.find(course => course.id === parseInt(eventData.course_id));
            if (selectedCourse && selectedCourse.max_students) {
              maxStudents = selectedCourse.max_students;
            }
          }
          
          // Format student count text
          const studentText = maxStudents ? 
            `0/${maxStudents} students` : 
            "0 students";
          
          // Add course and tutor name to the title if they are selected
          let classTitle = `${eventData.title} (Group Class`;
          
          // Add course info if available
          if (eventData.course_id) {
            const selectedCourse = courses.find(course => course.id === parseInt(eventData.course_id));
            if (selectedCourse) {
              classTitle += ` - ${selectedCourse.name}`;
            }
          }
          
          // Add student count
          classTitle += ` - ${studentText}`;
          
          // If a tutor is assigned, include their name
          if (eventData.tutor_id) {
            // Find the tutor object to get their name
            const selectedTutor = tutors.find(tutor => tutor.id === parseInt(eventData.tutor_id));
            if (selectedTutor) {
              classTitle += `, Tutor: ${selectedTutor.name}`;
            }
          }
          
          // Close the parenthesis
          classTitle += ')';
          
          eventData.title = classTitle;
        }
      
      let res;
      
      if (isEditing) {
        // Prepare the API endpoint and parameters for updating
        let endpoint = '/api/calendar/' + selectedEventId;
        const params = {};
        
        // If editing a recurring event
        if (currentEvent.recurring_id) {
          // Add the recurring_id and edit_mode parameters based on selected mode
          params.recurring_id = currentEvent.recurring_id;
          
          // Use the stored edit mode from the event object if available (when date was changed)
          // Otherwise use the editRecurringMode state value
          params.edit_mode = currentEvent._editMode || editRecurringMode; // 'single', 'future', or 'all'
          
          console.log('Using edit mode:', params.edit_mode);
        }
        
        // Update existing event with the appropriate parameters
        res = await axios.put(
          endpoint,
          eventData,
          {
            ...config,
            params
          }
        );
        
        // Show success message for edits
        if (currentEvent.recurring_id) {
          if (editRecurringMode === 'all') {
            showNotification('All events in the series updated successfully!', 'success');
          } else if (editRecurringMode === 'future') {
            showNotification('This and all future events updated successfully!', 'success');
          } else {
            showNotification('Event updated successfully!', 'success');
          }
        } else {
          showNotification('Event updated successfully!', 'success');
        }
      } else {
        // Create new event
        res = await axios.post('/api/calendar', eventData, config);
        
        // Show appropriate success message
        if (currentEvent.is_recurring) {
          const eventCount = res.data.events ? res.data.events.length : 0;
          showNotification(`Created ${eventCount} recurring events successfully!`, 'success');
        } else {
          showNotification('Event created successfully!', 'success');
        }
        
      }
      
      // Close modal and refresh events
      setShowEventModal(false);
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
      showNotification('Failed to save event. Please try again.', 'error');
    }
  };
  
  // Cancel event (soft delete with optional makeup)
  const handleCancelEvent = async (mode = 'single', scheduleMakeup = false) => {
    // Check if user has update permission
    if (!hasPermission('calendar', 'update')) {
      showNotification('You do not have permission to cancel calendar events', 'error');
      return;
    }
    
    // Check calendar view access level
    const viewAccess = getCalendarViewAccess();
    
    // For users with 'own' access, check if this is their event
    if (viewAccess === 'own' && viewingEvent.tutor_id !== JSON.parse(localStorage.getItem('user'))?.id) {
      showNotification('You do not have permission to cancel events that are not assigned to you', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      };
      
      // Add query parameters for recurring events
      if (viewingEvent.recurring_id && mode !== 'single') {
        config.params = {
          recurring_id: viewingEvent.recurring_id,
          cancel_mode: mode
        };
      }
      
      // Mark the event as cancelled
      await axios.post(
        `/api/calendar/${viewingEvent.id}/cancel`,
        {},
        config
      );
      
      // Show success message
      if (mode === 'all') {
        showNotification('All events in the series have been cancelled', 'success');
      } else if (mode === 'future') {
        showNotification('This and all future events in the series have been cancelled', 'success');
      } else {
        showNotification('Event cancelled successfully', 'success');
      }
      
      // Close modals
      setShowViewModal(false);
      setConfirmDelete(false);
      setShowRecurringDeleteModal(false);
      
      // If makeup event should be scheduled, open makeup modal
      if (scheduleMakeup) {
        setShowMakeupModal(true);
      } else {
        // Otherwise just refresh events
        fetchEvents();
      }
    } catch (err) {
      console.error('Error cancelling event:', err);
      showNotification('Failed to cancel event. Please try again.', 'error');
    }
  };
  
  // Handle makeup event scheduling
  const handleScheduleMakeup = async (makeupData) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      };
      
      // Schedule the makeup event - we don't need to call this endpoint again
      // since the MakeupEventModal already does that and sends us the results
      console.log('Received makeup event data from modal:', makeupData);
      
      // Success case - the scheduling has already happened in the modal
      showNotification('Makeup event has been scheduled successfully', 'success');
      setShowMakeupModal(false);
      
      // Refresh events to show the new makeup event
      fetchEvents();
    } catch (err) {
      console.error('Error processing makeup event:', err);
      
      // Show notification but note that the event might have been created
      showNotification(
        'There may have been an issue communicating the makeup event result, but the event may still have been created. Please check the calendar.',
        'warning'
      );
      
      // Close the modal and refresh events anyway
      setShowMakeupModal(false);
      fetchEvents();
    }
  };

  // Handle escape key press for modals
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') {
      // Close any open modal
      if (showEventModal) {
        setShowEventModal(false);
      } else if (showViewModal) {
        setShowViewModal(false);
      } else if (confirmDelete) {
        setConfirmDelete(false);
      } else if (showRecurringEditModal) {
        setShowRecurringEditModal(false);
      } else if (showRecurringDeleteModal) {
        setShowRecurringDeleteModal(false);
      } else if (showMakeupModal) {
        setShowMakeupModal(false);
      } else if (showBulkEmailModal) {
        setShowBulkEmailModal(false);
      } else if (showOutlookEventModal) {
        setShowOutlookEventModal(false);
      }
    }
  }, [showEventModal, showViewModal, confirmDelete, showRecurringEditModal, showRecurringDeleteModal, showMakeupModal, showBulkEmailModal, showOutlookEventModal]);

  // Add and remove escape key handler
  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);

  // Send reminders for a specific event
  const handleSendReminders = async (eventId) => {
    // Check permission to send reminders
    if (!hasPermission('calendar', 'update')) {
      alert('You do not have permission to send reminders');
      return;
    }
    
    try {
      setSendingReminders(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Use the correct backend URL (same as other API calls in this app)
      const response = await axios.post('/api/calendar/' + eventId + '/send-reminders', {}, config);
      
      showNotification(response.data.message || 'Reminders sent successfully', 'success');
      
      // Refresh event data to reflect that reminders have been sent
      fetchEvents();
    } catch (error) {
      console.error('Error sending reminders:', error);
      showNotification(
        error.response?.data?.message || 
        'Failed to send reminders. Please try again.',
        'error'
      );
    } finally {
      setSendingReminders(false);
    }
  };
  
  // Complete lesson (open attendance modal)
  const handleCompleteLesson = (eventId) => {
    // Check permission to complete lessons
    if (!hasPermission('calendar', 'complete')) {
      showNotification('You do not have permission to complete lessons', 'error');
      return;
    }
    
    // Check calendar view access level
    const viewAccess = getCalendarViewAccess();
    
    // For users with 'own' access, check if this is their event
    if (viewAccess === 'own' && viewingEvent.tutor_id !== JSON.parse(localStorage.getItem('user'))?.id) {
      showNotification('You do not have permission to complete lessons that are not assigned to you', 'error');
      return;
    }
    
    // Close the view modal and open the attendance modal
    setShowViewModal(false);
    setShowAttendanceModal(true);
  };
  
  // Handle completion from attendance modal
  const handleAttendanceComplete = async () => {
    // Close the attendance modal
    setShowAttendanceModal(false);
    
    // Show success message
    showNotification('Lesson marked as completed with attendance records', 'success');
    
    // Refresh the calendar events
    fetchEvents();
  };

  // Delete event
  const handleDeleteEvent = async (mode = 'single') => {
    // Check if user has delete permission
    if (!hasPermission('calendar', 'delete')) {
      showNotification('You do not have permission to delete calendar events', 'error');
      return;
    }
    
    // Check calendar view access level
    const viewAccess = getCalendarViewAccess();
    
    // For users with 'own' access, check if this is their event
    if (viewAccess === 'own' && viewingEvent.tutor_id !== JSON.parse(localStorage.getItem('user'))?.id) {
      showNotification('You do not have permission to delete events that are not assigned to you', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Add query parameters for recurring events
      if (viewingEvent.recurring_id && mode !== 'single') {
        config.params = {
          recurring_id: viewingEvent.recurring_id,
          delete_mode: mode
        };
      }
      
      await axios.delete(
        `/api/calendar/${viewingEvent.id}`,
        config
      );
      
      // Show success message
      if (mode === 'all') {
        showNotification('All events in the series have been deleted', 'success');
      } else if (mode === 'future') {
        showNotification('This and all future events in the series have been deleted', 'success');
      } else {
        showNotification('Event deleted successfully', 'success');
      }
      
      // Close modals and refresh events
      setShowViewModal(false);
      setConfirmDelete(false);
      setShowRecurringDeleteModal(false);
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      showNotification('Failed to delete event. Please try again.', 'error');
    }
  };
  
  // Edit event (from view modal)
  const handleEditEvent = () => {
    // Check if user has update permission
    if (!hasPermission('calendar', 'update')) {
      showNotification('You do not have permission to edit calendar events', 'error');
      return;
    }
    
    // Check calendar view access level
    const viewAccess = getCalendarViewAccess();
    
    // For users with 'own' access, check if this is their event
    if (viewAccess === 'own' && viewingEvent.tutor_id !== JSON.parse(localStorage.getItem('user'))?.id) {
      showNotification('You do not have permission to edit events that are not assigned to you', 'error');
      return;
    }
    
    // Added extensive debug logging
    console.log('EDIT EVENT DEBUGGING:');
    console.log('viewingEvent:', viewingEvent);
    console.log('viewingEvent.recurring_id:', viewingEvent.recurring_id);
    console.log('Type of recurring_id:', typeof viewingEvent.recurring_id);
    console.log('Stringified viewingEvent:', JSON.stringify(viewingEvent));
    
    // Try all possible checks to catch truthy recurring_id
    const checks = {
      directCheck: !!viewingEvent.recurring_id, 
      lengthCheck: viewingEvent.recurring_id && viewingEvent.recurring_id.length > 0,
      typeofCheck: typeof viewingEvent.recurring_id === 'string' && viewingEvent.recurring_id !== '',
      typeofNumber: typeof viewingEvent.recurring_id === 'number' && viewingEvent.recurring_id > 0,
      typeCheck: viewingEvent.recurring_id !== null && viewingEvent.recurring_id !== undefined && viewingEvent.recurring_id !== ''
    };
    
    console.log('All recurring_id checks:', checks);
    
    // Check if this event is part of a recurring series - strengthened check
    const isRecurring = viewingEvent.recurring_id !== null && 
                        viewingEvent.recurring_id !== undefined &&
                        viewingEvent.recurring_id !== '';
                        
    console.log('Is event recurring?', isRecurring);
    
    if (isRecurring) {
      console.log('RECURRING EVENT: Showing recurring edit modal');
      // Show the recurring edit mode selection modal
      setEditRecurringMode('single'); // default to single
      setShowRecurringEditModal(true);
    } else {
      console.log('REGULAR EVENT: Proceeding with normal edit');
      // Not a recurring event, proceed with normal edit
      startEditingEvent('single');
    }
  };
  
  // Start editing an event with the specified mode (single or all)
  const startEditingEvent = (mode) => {
    setEditRecurringMode(mode);
    
    // Make sure to properly parse values from viewingEvent
    // course_id and tutor_id need special handling to ensure they're correctly preserved
    const courseId = viewingEvent.course_id ? parseInt(viewingEvent.course_id, 10) : null;
    const tutorId = viewingEvent.tutor_id ? parseInt(viewingEvent.tutor_id, 10) : null;
    
    // Properly handle recurring_id - ensure it's not empty string or undefined
    const hasRecurringId = viewingEvent.recurring_id && viewingEvent.recurring_id !== '';
    const recurringId = hasRecurringId ? viewingEvent.recurring_id : null;
    
    // Handle public booking flag
    const isPublic = viewingEvent.is_public || false;
    
    // Get the max_students value from the event or from its student enrollment data
    const maxStudents = viewingEvent.max_students || 
                       (viewingEvent.studentEnrollment && viewingEvent.studentEnrollment.maxStudents) || 
                       null;
    
    console.log('Editing event with course_id:', courseId, 'original value:', viewingEvent.course_id);
    console.log('Event recurring_id:', recurringId);
    console.log('Is event public:', isPublic);
    console.log('Max students value:', maxStudents);
    
    setCurrentEvent({
      title: viewingEvent.title,
      description: viewingEvent.description || '',
      start_time: new Date(viewingEvent.start),
      end_time: new Date(viewingEvent.end),
      location: viewingEvent.location || '',
      country: viewingEvent.country || '',
      event_type: viewingEvent.event_type || 'course',
      related_lead_id: viewingEvent.related_lead_id || null,
      related_deal_id: viewingEvent.related_deal_id || null,
      is_recurring: false, // Editing a single event, not the series
      recurrence_count: 1,
      recurrence_frequency: 'weekly',
      tutor_id: tutorId,
      course_id: courseId,
      recurring_id: recurringId,
      status: viewingEvent.status || 'Scheduled', // Preserve the event's status
      is_public: isPublic, // Include public booking flag
      payment_required: viewingEvent.payment_required || false, // Include payment required flag
      price: viewingEvent.price || null, // Include price
      currency: viewingEvent.currency || 'USD', // Include currency
      send_tutor_reminder: viewingEvent.send_tutor_reminder || true,
      send_student_reminder: viewingEvent.send_student_reminder || true,
      max_students: maxStudents, // Include max_students value
      _editModeSelected: true, // Add a flag to indicate edit mode has been selected
      _editMode: mode // Remember the selected edit mode
    });
    
    setSelectedEventId(viewingEvent.id);
    setIsEditing(true);
    setShowViewModal(false);
    setShowRecurringEditModal(false);
    setShowEventModal(true);
  };
  
  // Calendar event style
  const eventStyleGetter = (event) => {
    let backgroundColor;

    // **New:** Check if it's a course without a tutor first
    if (event.event_type === 'course' && !event.tutor_id) {
      backgroundColor = '#EF4444'; // Red for unassigned courses
    }
    // Original logic: Check if it's a course event with a tutor assigned
    else if (event.event_type === 'course' && event.tutor_id && tutorColors[event.tutor_id]) {
      // Use tutor-specific color for course events with assigned tutors
      backgroundColor = tutorColors[event.tutor_id];
    } else {
      // Fall back to event type color for other events (non-course, or course with tutor but no color)
      backgroundColor = event.event_type ? 
        (eventTypeColors[event.event_type] || eventTypeColors.course) : 
        eventTypeColors.course;
    }
    
    // For course events, check if it's at or above capacity
    let style = {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    
    // Special styling based on status
    if (event.status === 'Completed') {
      style.opacity = 1;
      style.borderLeft = '5px solid rgba(16, 185, 129, 0.9)'; // Green border for completed events
      style.backgroundColor = '#059669'; // Darker green
    } else if (event.status === 'Cancelled') {
      style.opacity = 0.5;
      style.textDecoration = 'line-through';
      style.backgroundColor = '#6B7280'; // Gray color for cancelled events
      style.borderLeft = '5px solid rgba(239, 68, 68, 0.9)'; // Red border for cancelled events
    } else {
      // Special styling for course events at capacity (only for scheduled events)
      if (event.event_type === 'course' && 
          event.studentEnrollment && 
          event.studentEnrollment.currentCount >= event.studentEnrollment.maxStudents) {
        style.borderLeft = '5px solid rgba(239, 68, 68, 0.9)'; // Red border for full courses
      }
    }
    
    return { style };
  };
  
  // Custom event title formatter to include student enrollment count
  const eventTitleAccessor = (event) => {
    if (event.event_type === 'course' && event.studentEnrollment) {
      return `${event.title} (${event.studentEnrollment.currentCount}/${event.studentEnrollment.maxStudents})`;
    }
    return event.title;
  };
  
  // If calendar is completely loading (first load)
  if (loading && events.length === 0) {
    return (
      <div className="p-8 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-lg font-medium text-gray-700">Loading calendar...</div>
      </div>
    );
  }
  
  if (error) {
    return <div className="text-center p-8 text-red-500 bg-red-50 border border-red-200 rounded-lg">{error}</div>;
  }
  
  return (
    <div className="p-4">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <h3 className="text-lg font-semibold mr-2 flex items-center">Calendar</h3>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className={`px-4 py-2 flex items-center ${ 
                activeFilterCount > 0 ? '!bg-blue-100 !text-blue-800 !border-blue-300' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
              {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
            </Button>
            
            {/* Dynamically render country quick filter buttons based on company settings */}
            {companyCountries.length > 1 && companyCountries.map(country => {
              // Map of country names to flag emojis
              const countryFlags = {
                'Norway': '🇳🇴',
                'Sweden': '🇸🇪',
                'Germany': '🇩🇪',
                'United States': '🇺🇸',
                'Austria': '🇦🇹',
                'Switzerland': '🇨🇭',
                'Luxembourg': '🇱🇺',
                'Denmark': '🇩🇰',
                'Finland': '🇫🇮'
              };

              return (
                <Button
                  key={country}
                  onClick={() => quickFilterByCountry(country)}
                  variant="outline"
                  className={`px-4 py-2 flex items-center ${
                    activeFilters.country === country ? '!bg-blue-100 !text-blue-800 !border-blue-300' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                  title={`Filter by ${country}`}
                >
                  {countryFlags[country] || ''} {country}
                </Button>
              );
            })}
          </div>
          
          <div className="flex space-x-2">
            <Button
              onClick={() => setShowEventsListModal(true)}
              variant="secondary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              Find All Events
            </Button>
            
            {hasPermission('calendar', 'create') && (
              <Button
                onClick={() => {
                  // Create default start and end times for today
                  const today = new Date();
                  const defaultStartTime = new Date(today);
                  defaultStartTime.setHours(17, 0, 0); // Set to 17:00 (5:00 PM)
                  
                  const defaultEndTime = new Date(today);
                  defaultEndTime.setHours(18, 30, 0); // Set to 18:30 (6:30 PM)
                  
                  setCurrentEvent({
                    title: '',
                    description: '',
                    start_time: defaultStartTime,
                    end_time: defaultEndTime,
                    location: '',
                    country: '',
                    event_type: 'course', // Default to course
                    related_lead_id: null,
                    related_deal_id: null,
                    is_recurring: false,
                    recurrence_count: 1,
                    recurrence_frequency: 'weekly',
                    tutor_id: null,
                    course_id: null,
                    status: 'Scheduled', // Default status for new events
                    send_tutor_reminder: true,
                    send_student_reminder: true
                  });
                  setIsEditing(false);
                  setShowEventModal(true);
                }}
                variant="primary"
              >
                Add Event
              </Button>
            )}
          </div>
        </div>
        
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Filter Calendar Events</h3>
              <Button 
                onClick={clearFilters}
                variant="link"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear all filters
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <select
                  name="country"
                  value={filters.country}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Countries</option>
                  <option value="Norway">Norway</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Germany">Germany</option>
                  <option value="United States">United States</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  placeholder="Filter by location"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tutor
                </label>
                <select
                  name="tutor_id"
                  value={filters.tutor_id}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Tutors</option>
                  <option value="no_tutor">No Tutor Assigned</option>
                  {tutors.map(tutor => (
                    <option key={tutor.id} value={tutor.id}>
                      {tutor.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course
                </label>
                <select
                  name="course_id"
                  value={filters.course_id}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  name="event_type"
                  value={filters.event_type}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Event Types</option>
                  <option value="course">Course</option>
                  <option value="demo">Demo</option>
                  <option value="sales call">Sales Call</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student Name
                </label>
                <input
                  type="text"
                  name="student_name"
                  value={filters.student_name}
                  onChange={handleFilterChange}
                  placeholder="Filter by student"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Email
                </label>
                <input
                  type="text"
                  name="parent_email"
                  value={filters.parent_email}
                  onChange={handleFilterChange}
                  placeholder="Filter by parent email"
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="mt-4 space-y-2"> {/* Added space-y-2 for spacing */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="recurring_start_only"
                  name="recurring_start_only"
                  checked={filters.recurring_start_only}
                  onChange={handleFilterChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={filters.recurring_end_only} // Disable if end_only is checked
                />
                <label htmlFor="recurring_start_only" className={`ml-2 block text-sm ${filters.recurring_end_only ? 'text-gray-400' : 'text-gray-700'}`}>
                  Show only first events in recurring series
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="recurring_end_only"
                  name="recurring_end_only"
                  checked={filters.recurring_end_only}
                  onChange={handleFilterChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={filters.recurring_start_only} // Disable if start_only is checked
                />
                <label htmlFor="recurring_end_only" className={`ml-2 block text-sm ${filters.recurring_start_only ? 'text-gray-400' : 'text-gray-700'}`}>
                  Show only last events in recurring series
                </label>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button
                onClick={applyFilters}
                variant="primary"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <div className="rounded-lg shadow-md">
        <div className="p-4 bg-white">
          <div className="mb-4">
            <div className="flex flex-col md:flex-row md:items-center">
              <div className="md:mr-8">
                <div className="flex items-center mb-2 md:mb-0">
                  <h4 className="text-sm font-semibold mr-3">Event Types:</h4>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(eventTypeColors).map(([type, color]) => (
                      <div key={type} className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-2"
                          style={{ backgroundColor: color }}
                        ></div>
                        <span className="text-sm capitalize">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {tutors.length > 0 && (
                <div>
                  <div className="flex items-center">
                    <h4 className="text-sm font-semibold mr-3">Tutors:</h4>
                    <div className="flex flex-wrap gap-4 max-h-48 overflow-y-auto pr-2" style={{ maxWidth: 'calc(100% - 4rem)' }}>
                      {tutors.map(tutor => (
                        <div key={tutor.id} className="flex items-center mb-2">
                          <div
                            className="w-4 h-4 rounded-full mr-2"
                            style={{ backgroundColor: tutorColors[tutor.id] || '#cccccc' }}
                          ></div>
                          <span className="text-sm">{tutor.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <style dangerouslySetInnerHTML={{ __html: styles }} />
          <div className="bg-white overflow-visible relative">
            {/* Show overlay loading indicator when more events are being fetched */}
            {loading && events.length > 0 && (
              <div className="absolute inset-0 bg-white bg-opacity-70 z-50 flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                  <div className="text-sm font-medium text-gray-700">Updating calendar...</div>
                </div>
              </div>
            )}
            
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              onSelectEvent={handleEventClick}
              onSelectSlot={hasPermission('calendar', 'create') ? handleSlotSelect : undefined}
              selectable={hasPermission('calendar', 'create')}
              eventPropGetter={eventStyleGetter}
              titleAccessor={eventTitleAccessor}
              views={['month', 'week', 'day']}
              defaultView="month"
              date={calendarDate}
              onNavigate={(date, view) => handleCalendarNavigate(date, view)}
              onView={(view) => {
                // Update view type in active filters when view changes
                const updatedFilters = { ...activeFilters, view_type: view };
                setActiveFilters(updatedFilters);
                localStorage.setItem('calendarActiveFilters', JSON.stringify(updatedFilters));
                // Re-fetch events with the new view
                fetchEvents(calendarDate);
              }}
              showAllEvents={true}
              popup={false}
              style={{ height: 'auto', minHeight: '600px' }}
            />
          </div>
        </div>
      </div>
      
      <EventFormModal
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
        }}
        currentEvent={currentEvent}
        setCurrentEvent={setCurrentEvent}
        isEditing={isEditing}
        selectedEventId={selectedEventId}
        handleSaveEvent={handleSaveEvent}
        leads={leads}
        deals={deals}
        tutors={tutors}
        courses={courses}
      />
      
      <EventDetailsModal 
        isOpen={showViewModal}
        onClose={(action) => {
          setShowViewModal(false);
          if (action === 'manage-students') {
            setShowManageStudentsModal(true);
          } else if (action === 'email-students') {
            setShowBulkEmailModal(true);
          }
        }}
        viewingEvent={viewingEvent}
        studentEnrollment={studentEnrollment}
        eventTypeColors={eventTypeColors}
        sendingReminders={sendingReminders}
        handleSendReminders={handleSendReminders}
        handleCompleteLesson={handleCompleteLesson}
        handleEditEvent={handleEditEvent}
        handleCancelEvent={handleCancelEvent}
        handleDeleteClick={() => {
          if (viewingEvent?.recurring_id) {
            setDeleteRecurringMode('single');
            setShowRecurringDeleteModal(true);
          } else {
            setConfirmDelete(true);
          }
        }}
      />
      
      <DeleteConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => handleDeleteEvent('single')}
      />
      
      <RecurringDeleteModal
        isOpen={showRecurringDeleteModal}
        onClose={() => setShowRecurringDeleteModal(false)}
        deleteRecurringMode={deleteRecurringMode}
        setDeleteRecurringMode={setDeleteRecurringMode}
        onConfirm={(mode) => handleDeleteEvent(mode)}
      />
      
      <RecurringEditModal
        isOpen={showRecurringEditModal}
        onClose={() => setShowRecurringEditModal(false)}
        editRecurringMode={editRecurringMode}
        setEditRecurringMode={setEditRecurringMode}
        onConfirm={(mode) => startEditingEvent(mode)}
      />
      
      <EventsListModal 
        isOpen={showEventsListModal}
        onClose={() => setShowEventsListModal(false)}
        onSelectEvent={(event) => {
          setCalendarDate(new Date(event.start_time));
          setShowEventsListModal(false);
          setViewingEvent(event);
          setShowViewModal(true);
        }}
      />
      
      {viewingEvent && (
        <ManageStudentsModal
          isOpen={showManageStudentsModal}
          onClose={() => {
            setShowManageStudentsModal(false);
            
            if (viewingEvent && viewingEvent.event_type === 'course') {
              const fetchStudentEnrollment = async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.get('/api/calendar/' + viewingEvent.id + '/students', {
                    headers: {
                      'x-auth-token': token
                    }
                  });
                  
                  setStudentEnrollment({
                    currentCount: response.data.current_count || 0,
                    maxStudents: response.data.max_students || 10
                  });
                } catch (error) {
                  console.error('Error fetching student enrollment:', error);
                }
              };
              
              fetchStudentEnrollment();
            }
          }}
          eventId={viewingEvent.id}
          onSave={(shouldClose = true) => {
            fetchEvents();
            if (shouldClose) {
              setShowManageStudentsModal(false);
            }
          }}
        />
      )}

      {viewingEvent && (
        <BulkEmailModal
          isOpen={showBulkEmailModal}
          onClose={(success) => {
            setShowBulkEmailModal(false);
            if (success) {
              showNotification('Emails sent successfully to enrolled parents!', 'success');
            }
          }}
          eventId={viewingEvent.id}
          eventTitle={viewingEvent.title}
        />
      )}
      
      {viewingEvent && (
        <AttendanceModal
          isOpen={showAttendanceModal}
          onClose={() => setShowAttendanceModal(false)}
          eventId={viewingEvent.id}
          onComplete={handleAttendanceComplete}
        />
      )}
      
      {viewingEvent && (
        <MakeupEventModal
          isOpen={showMakeupModal}
          onClose={() => setShowMakeupModal(false)}
          originalEvent={viewingEvent}
          onSave={handleScheduleMakeup}
          tutors={tutors}
          courses={courses}
        />
      )}
      
      {/* Outlook Event Details Modal */}
      {selectedOutlookEvent && (
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 ${
            showOutlookEventModal ? 'block' : 'hidden'
          }`}
          onClick={() => setShowOutlookEventModal(false)}
        >
          <div 
            className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900">Outlook Event</h3>
                </div>
                <button
                  onClick={() => setShowOutlookEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Event Details */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {selectedOutlookEvent.title}
                  </h4>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Start Time:</span>
                    <p className="text-sm text-gray-900">
                      {moment(selectedOutlookEvent.start).format('LLLL')}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-500">End Time:</span>
                    <p className="text-sm text-gray-900">
                      {moment(selectedOutlookEvent.end).format('LLLL')}
                    </p>
                  </div>

                  {selectedOutlookEvent.location && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Location:</span>
                      <p className="text-sm text-gray-900">{selectedOutlookEvent.location}</p>
                    </div>
                  )}

                  {selectedOutlookEvent.description && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Description:</span>
                      <p className="text-sm text-gray-900">{selectedOutlookEvent.description}</p>
                    </div>
                  )}

                  {selectedOutlookEvent.isAllDay && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-blue-600 font-medium">All Day Event</span>
                    </div>
                  )}
                </div>

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm">
                      <p className="text-blue-800 font-medium mb-1">Read-only Outlook Event</p>
                      <p className="text-blue-700">
                        This event is synced from Microsoft Outlook and cannot be edited in the CRM. 
                        To make changes, please edit the event in Microsoft Outlook.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowOutlookEventModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;